import { baseCurrency } from '../config';
import { urlify, splitNewLine } from '../utils/string';

export default (state = {}, action = {}) => {
	if (action.type === 'PRODUCT_CACHE') {
		// Create lookup tables.
		// Sorting done dynamically.

		// Name - Search
		const cache = action.productCache.content;
		const { languages, categories, categoryTitles, categoryTitlesLocalized, productIdLookup } = action.productCache; // Languages WP setting not Centra.
		const filterArrays = {
			categories, // Object mapping category paths to arrays of product keys.
			categoryVariants: {},
			filterCategories: {},
			colors: {}, // Object mapping color names to arrays of product keys.
			finishes: {}, // Object mapping color names to arrays of product keys.
			productsOnSale: [], // Array of product keys
			newProducts: [], // Array of product keys
		};
		const filterDescriptions = {
			categories: categoryTitles,
			filterCategories: {},
			colors: {},
			finishes: {},
			keyWords: {},
		};
		const languageKeys = Object.keys(languages).map((l) => l.toLowerCase());
		const productVariantLookup = {};
		const searchTerms = languageKeys.reduce((acc, language) => ({ ...acc, [language]: {} }), {});

		const localized = languageKeys.slice(1).reduce((acc, language) => ({
			...acc,
			[language]: {
				categories: categoryTitlesLocalized ? categoryTitlesLocalized[language] : {},
				filterCategories: {},
				colors: {},
				finishes: {},
			},
		}), {}); // Merged localized version of above.

		/*
			Localized versions shouldn't create new arrays.
			Their arrays exist but at their non-translated key.
			How to map their keys with their localized versions.
		*/

		Object.keys(cache).forEach((cacheKey) => {
			const item = cache[cacheKey];
			if (item.productHidden) return;

			// Category lookup
			if (item.categories) {
				item.categories.forEach((category) => {
					category.uri.split('/').forEach((pathSection, i) => {
						const categoryUri = category.uri.split('/').reduce((acc, path, j) => (i >= j ? `${acc}/${path}` : acc), '').slice(1);

						if (!filterArrays.categoryVariants[categoryUri]) {
							filterArrays.categoryVariants[categoryUri] = [];
						}
						filterArrays.categoryVariants[categoryUri][filterArrays.categoryVariants[categoryUri].length] = cacheKey;
					});
				});
			}

			if (item.sizes) {
				item.sizes.forEach((size) => {
					productVariantLookup[size.id.slice(size.id.lastIndexOf('/') + 1)] = cacheKey;
				});
			}

			if (item.variantCategories) {
				item.variantCategories.filter((c) => !!c).forEach((category) => {
					category.split('/').forEach((pathSection, i) => {
						const categoryUri = category.split('/').reduce((acc, path, j) => (i >= j ? `${acc}/${path}` : acc), '').slice(1);

						if (!filterArrays.categoryVariants[categoryUri]) {
							filterArrays.categoryVariants[categoryUri] = [];
						}
						filterArrays.categoryVariants[categoryUri][filterArrays.categoryVariants[categoryUri].length] = cacheKey;
					});
				});
			}

			// Category lookup
			if (item.filterCategory) {
				splitNewLine(item.filterCategory).filter((fC) => fC !== '').forEach((categoryItem) => {
					const categoryItemUrlified = urlify(categoryItem);

					if (!filterArrays.filterCategories[categoryItemUrlified]) {
						filterArrays.filterCategories[categoryItemUrlified] = [];
						filterDescriptions.filterCategories[categoryItemUrlified] = categoryItem;
					}
					filterArrays.filterCategories[categoryItemUrlified][filterArrays.filterCategories[categoryItemUrlified].length] = cacheKey;
				});
			}

			// Colors lookup
			if (item.filterColor) {
				splitNewLine(item.filterColor).filter((fC) => fC !== '').map((fC) => String(fC).split(',')).forEach(([color, order, description]) => {
					const colorItem = { color, order, description };
					const colorItemUrlified = urlify(colorItem.description);

					if (!filterArrays.colors[colorItemUrlified]) {
						filterArrays.colors[colorItemUrlified] = [];
						filterDescriptions.colors[colorItemUrlified] = colorItem;
					}
					filterArrays.colors[colorItemUrlified][filterArrays.colors[colorItemUrlified].length] = cacheKey;
				});
			}

			// Finishes lookup
			if (item.filterFinish) {
				const finishItemUrlified = urlify(item.filterFinish);
				if (!filterArrays.finishes[finishItemUrlified]) {
					filterArrays.finishes[finishItemUrlified] = [];
					filterDescriptions.finishes[finishItemUrlified] = item.filterFinish;
				}
				filterArrays.finishes[finishItemUrlified][filterArrays.finishes[finishItemUrlified].length] = cacheKey;
			}

			// Search Terms
			searchTerms[languageKeys[0]][item.name + item.variant] = searchTerms[languageKeys[0]][item.name + item.variant] ? [...searchTerms[languageKeys[0]][item.name + item.variant], cacheKey] : [cacheKey];

			if (item.keyWords) {
				splitNewLine(item.keyWords).forEach((kW) => {
					searchTerms[languageKeys[0]][kW] = searchTerms[languageKeys[0]][kW] ? [...searchTerms[languageKeys[0]][kW], cacheKey] : [cacheKey];
				});
			}

			// Sale / News
			const price = item.price?.[baseCurrency];
			if (price && price.compareAtPrice && price.price !== price.compareAtPrice) filterArrays.productsOnSale[filterArrays.productsOnSale.length] = cacheKey;
			if (item.tags && item.tags.indexOf('new') !== -1) filterArrays.newProducts[filterArrays.newProducts.length] = cacheKey;
		});

		/*
			Extend search terms.
		*/

		// Categories
		Object.keys(filterDescriptions.categories).forEach((category) => {
			const categorySearchTerm = searchTerms[languageKeys[0]][filterDescriptions.categories[category]];
			searchTerms[languageKeys[0]][filterDescriptions.categories[category]] = !categorySearchTerm ? filterArrays.categoryVariants[category] : [...categorySearchTerm, ...filterArrays.categoryVariants[category]];

			if (languageKeys.length > 1) {
				languageKeys.slice(1).forEach((language) => {
					if (localized[language] && localized[language].categories && localized[language].categories[category]) {
						const localizedCategorySearchTerm = searchTerms[language][localized[language].categories[category].description];
						searchTerms[language][localized[language].categories[category].description] = !localizedCategorySearchTerm ? filterArrays.categoryVariants[category] : [...localizedCategorySearchTerm, ...filterArrays.categoryVariants[category]];
					} else {
						const localizedCategorySearchTerm = searchTerms[language][filterDescriptions.categories[category]];
						searchTerms[language][filterDescriptions.categories[category]] = !localizedCategorySearchTerm ? filterArrays.categoryVariants[category] : [...localizedCategorySearchTerm, ...filterArrays.categoryVariants[category]];
					}
				});
			}
		});

		// Colors
		Object.keys(filterDescriptions.colors).forEach((color) => {
			const colorSearchTerm = searchTerms[languageKeys[0]][filterDescriptions.colors[color].description];
			searchTerms[languageKeys[0]][filterDescriptions.colors[color].description] = !colorSearchTerm ? filterArrays.colors[color] : [...colorSearchTerm, ...filterArrays.colors[color]];

			if (languageKeys.length > 1) {
				languageKeys.slice(1).forEach((language) => {
					if (localized?.[language]?.colors?.[color]) {
						const localizedColorSearchTerm = searchTerms[language][localized[language].colors[color].description];
						searchTerms[language][localized[language].colors[color].description] = !localizedColorSearchTerm ? filterArrays.colors[color] : [...localizedColorSearchTerm, ...filterArrays.colors[color]];
					} else {
						const localizedColorSearchTerm = searchTerms[language][filterDescriptions.colors[color].description];
						searchTerms[language][filterDescriptions.colors[color].description] = !localizedColorSearchTerm ? filterArrays.colors[color] : [...localizedColorSearchTerm, ...filterArrays.colors[color]];
					}
				});
			}
		});

		// Finishes
		Object.keys(filterDescriptions.finishes).forEach((finish) => {
			const finishSearchTerm = searchTerms[languageKeys[0]][filterDescriptions.finishes[finish]];
			searchTerms[languageKeys[0]][filterDescriptions.finishes[finish]] = !finishSearchTerm ? filterArrays.finishes[finish] : [...finishSearchTerm, ...filterArrays.finishes[finish]];

			if (languageKeys.length > 1) {
				languageKeys.slice(1).forEach((language) => {
					if (localized?.[language]?.finishes?.[finish]) {
						const localizedFinishSearchTerm = searchTerms[language][localized[language].finishes[finish]];
						searchTerms[language][localized[language].finishes[finish]] = !localizedFinishSearchTerm ? filterArrays.finishes[finish] : [...localizedFinishSearchTerm, ...filterArrays.finishes[finish]];
					} else {
						const localizedFinishSearchTerm = searchTerms[language][filterDescriptions.finishes[finish]];
						searchTerms[language][filterDescriptions.finishes[finish]] = !localizedFinishSearchTerm ? filterArrays.finishes[finish] : [...localizedFinishSearchTerm, ...filterArrays.finishes[finish]];
					}
				});
			}
		});

		// Key Words


		// Sort category variants by categories.
		filterArrays.categoryVariants = Object.keys(filterArrays.categoryVariants).reduce((acc, cat) => {
			if (filterArrays.categories[cat]) {
				let categorySort = filterArrays.categories.shop;
				if (!Array.isArray(categorySort)) categorySort = Object.values(filterArrays.categories.shop);
				acc = {
					...acc,
					[cat]: filterArrays.categoryVariants[cat].sort((a, b) => {
						const cvA = a.indexOf('_') !== -1 ? a.slice(0, a.indexOf('_')) : a;
						const cvB = b.indexOf('_') !== -1 ? b.slice(0, b.indexOf('_')) : b;
						const indexA = categorySort.indexOf(parseInt(cvA, 10));
						const indexB = categorySort.indexOf(parseInt(cvB, 10));
						if (indexA === indexB) {
							if (cache?.[a]?.color && cache?.[b]?.color) {
								const [, cOrderA = ''] = (cache[a].color || '').split(',');
								const [, cOrderB = ''] = (cache[b].color || '').split(',');
								return cOrderA.localeCompare(cOrderB);
							}
							return 0;
						}
						if (indexA > indexB) return 1;
						return -1;
					}),
				};
			}
			return acc;
		}, {});

		return {
			...state,
			[action.market]: {
				...state[action.market],
				[action.pricelist]: {
					products: cache,
					productIdLookup,
					productVariantLookup,
					filterArrays,
					filterDescriptions,
					localized,
					searchTerms,
				},
			},
		};
	}

	return state;
};
